import { useEffect, useRef, useState } from 'react';
import { useNavigation } from 'react-router';

import styles from './route-progress.component.module.css';

// Show the bar only once a navigation has been pending this long, so instant
// (prefetched) transitions never flash it.
const SHOW_DELAY = 120;
const HIDE_DELAY = 220;

/**
 * A slim top-of-viewport progress bar that appears while a route navigation is
 * in flight. Prefetched links resolve before SHOW_DELAY so the bar stays hidden;
 * only cold navigations (a real loader round-trip) get the moving cue, which is
 * what makes a slower page swap still feel responsive. Renders nothing at rest,
 * so SSR and the first client paint agree.
 */
export function RouteProgress() {
	const navigation = useNavigation();
	const active = navigation.state !== 'idle';
	const [visible, setVisible] = useState(false);
	const [progress, setProgress] = useState(0);
	const visibleRef = useRef(false);

	useEffect(() => {
		visibleRef.current = visible;
	}, [visible]);

	useEffect(() => {
		if (active) {
			const show = setTimeout(() => {
				setVisible(true);
				setProgress(8);
			}, SHOW_DELAY);
			return () => clearTimeout(show);
		}
		if (!visibleRef.current) {
			return;
		}
		// Finished: run to 100%, then fade out.
		setProgress(100);
		const hide = setTimeout(() => {
			setVisible(false);
			setProgress(0);
		}, HIDE_DELAY);
		return () => clearTimeout(hide);
	}, [active]);

	// Trickle towards (but never reaching) the end while still loading.
	useEffect(() => {
		if (!visible || !active) {
			return;
		}
		const tick = setInterval(() => {
			setProgress((p) => (p >= 90 ? p : p + (90 - p) * 0.12));
		}, 200);
		return () => clearInterval(tick);
	}, [visible, active]);

	if (!visible) {
		return null;
	}
	return (
		<div className={styles.track} aria-hidden>
			<div
				className={styles.bar}
				data-done={progress >= 100 || undefined}
				style={{ width: `${progress}%` }}
			/>
		</div>
	);
}
